import React from 'react';
import {
  Button,
  ButtonProps,
  capitalize,
  makeStyles,
  Theme,
} from '@material-ui/core';
import classNames from 'classnames';

import {
  isPaletteColor,
  generateButtonColorStyles,
} from 'utils/muiStyles/utils';
import { ThemeColor } from 'utils/muiStyles/types';

function generateThemeColorStyles(theme: Theme) {
  return (
    Object.keys(theme.palette)
      .filter(color => isPaletteColor(color, theme))
      // Primary and secondary styles already exist
      .filter(color => !['primary', 'secondary'].includes(color))
      .reduce((styles, colorName) => {
        return {
          ...styles,
          ...generateButtonColorStyles(colorName, theme),
        };
      }, {})
  );
}

const useStyles = makeStyles(generateThemeColorStyles, {
  classNamePrefix: 'MuiButton',
});

function isDefaultMuiButtonColor(
  color: string,
): color is Required<ButtonProps>['color'] {
  return ['inherit', 'primary', 'secondary', 'default'].includes(color);
}

export type MuiButtonProps = Omit<ButtonProps, 'color'> & {
  color: ThemeColor;
};

/**
 * Proxy for MUI Button that overrides "color" prop type
 */
function MuiButton({ color, variant, ...rest }: MuiButtonProps) {
  const classes = useStyles();
  let muiColor: ButtonProps['color'];
  if (isDefaultMuiButtonColor(color)) {
    muiColor = color;
  }
  return (
    <Button
      {...rest}
      variant={variant}
      color={muiColor}
      className={classNames(
        rest.className,
        classes[`${variant}${capitalize(color)}`],
      )}
    />
  );
}

export default MuiButton;
